import { exportElementToPdf } from '../../../utils/exportElementToPdf'

const DEFAULT_CLINIC = 'CLÍNICA MÉDICA INTEGRADA'
const GENERIC_CLINIC = new Set(['empresa atual', 'organização', 'organizacao', 'organization'])

function resolveClinicName(name) {
  const trimmed = String(name || '').trim()
  if (!trimmed || GENERIC_CLINIC.has(trimmed.toLowerCase())) return DEFAULT_CLINIC
  return trimmed
}

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function fileSlug(value) {
  return String(value || 'escala')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'escala'
}

const SHEET_CSS = `
  .duty-print-sheet { width: 1040px; background: #fff; color: #0f172a; font-family: Arial, Helvetica, sans-serif; padding: 28px 32px 36px; }
  .duty-print-header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #0f766e; padding-bottom: 12px; }
  .duty-print-brand { display: flex; gap: 12px; align-items: center; }
  .duty-print-logo { width: 42px; height: 42px; border-radius: 10px; background: #0f766e; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; }
  .duty-print-header h1 { margin: 0; font-size: 18px; }
  .duty-print-header p { margin: 2px 0 0; color: #475569; font-size: 12px; }
  .duty-print-side { text-align: right; font-size: 12px; color: #334155; }
  .duty-print-side p { margin: 0 0 2px; }
  .duty-print-title { margin: 18px 0 4px; font-size: 20px; }
  .duty-print-meta { margin: 0 0 14px; color: #475569; font-size: 13px; }
  .duty-print-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .duty-print-table th { text-align: left; background: #f0fdfa; color: #115e59; padding: 7px 8px; border-bottom: 1px solid #99f6e4; }
  .duty-print-table td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  .duty-print-footer { margin-top: 16px; font-size: 11px; color: #64748b; }
`

function sheetMarkup(model) {
  const clinicName = resolveClinicName(model.clinicName)
  const letters = clinicName.split(/\s+/).filter((word) => !/^(de|da|do|e)$/i.test(word)).slice(0, 2).map((word) => word[0]?.toUpperCase() || '').join('') || 'CM'
  const head = model.columns.map((column) => `<th>${esc(column)}</th>`).join('')
  const body = model.rows.length
    ? model.rows.map((row) => `<tr>${row.map((cell) => `<td>${esc(cell)}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${model.columns.length}">Nenhum plantão neste mês.</td></tr>`
  return `<article class="duty-print-sheet">
    <header class="duty-print-header">
      <div class="duty-print-brand">
        <div class="duty-print-logo">${esc(letters)}</div>
        <div>
          <h1>${esc(clinicName)}</h1>
          <p>Escala de plantões</p>
        </div>
      </div>
      <div class="duty-print-side">
        <p><strong>Emissão</strong> ${esc(model.issuedAt)}</p>
        <p><strong>Emitido por</strong> ${esc(model.issuedBy || '—')}</p>
      </div>
    </header>
    <h2 class="duty-print-title">${esc(model.title)}</h2>
    <p class="duty-print-meta">${esc(model.subtitle)}</p>
    <table class="duty-print-table">
      <thead><tr>${head}</tr></thead>
      <tbody>${body}</tbody>
    </table>
    <footer class="duty-print-footer">${esc(clinicName)} · ${esc(model.issuedAt)}</footer>
  </article>`
}

function printDocument(model) {
  const win = window.open('', '_blank', 'width=1100,height=800')
  if (!win) return false
  const title = esc(model.title)
  win.document.open()
  win.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    ${SHEET_CSS}
    body { margin: 0; background: #f8fafc; }
    .toolbar { display: flex; gap: 8px; padding: 12px 16px; background: #fff; border-bottom: 1px solid #e2e8f0; }
    .toolbar button { border: 0; border-radius: 8px; background: #0f766e; color: #fff; font-weight: 700; padding: 8px 14px; cursor: pointer; }
    .toolbar .secondary { background: #e2e8f0; color: #0f172a; }
    @page { size: landscape; margin: 10mm; }
    @media print { .toolbar { display: none; } body { background: #fff; } .duty-print-sheet { width: auto; padding: 0; } }
  </style>
</head>
<body>
  <div class="toolbar">
    <button type="button" onclick="window.print()">Imprimir</button>
    <button type="button" class="secondary" onclick="window.close()">Fechar</button>
  </div>
  ${sheetMarkup(model)}
  <script>window.addEventListener('load', function () { setTimeout(function () { window.print() }, 150) })</script>
</body>
</html>`)
  win.document.close()
  win.focus()
  return true
}

async function downloadDocument(model, filename) {
  const host = document.createElement('div')
  host.style.cssText = 'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;background:#fff;'
  host.innerHTML = `<style>${SHEET_CSS}</style>${sheetMarkup(model)}`
  document.body.appendChild(host)
  try {
    const sheet = host.querySelector('.duty-print-sheet')
    await exportElementToPdf(sheet, filename, {
      orientation: 'l',
      rowSelector: '.duty-print-table tbody tr',
      scale: 2
    })
  } finally {
    host.remove()
  }
}

export function rosterFileName(label, monthName, year) {
  return `Escala-${fileSlug(label)}-${fileSlug(monthName)}-${year}.pdf`
}

export function printDutySheet(model) {
  return printDocument(model)
}

export function downloadDutySheet(model, filename) {
  return downloadDocument(model, filename)
}
